class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

interface AuthServiceConfig {
  baseUrl?: string;
  iamUrl?: string;
  tokenRefreshThreshold?: number;
  storage?: Storage;
}

interface TokenPayload {
  exp: number;
  [key: string]: unknown;
}

class AuthService {
  static TOKEN_TYPES = {
    ACCESS: "vivoAccessToken",
    REFRESH: "vivoRefreshToken",
  };

  // sessionStorage keys used to persist an active superuser impersonation.
  // Owned here so logout can always tear impersonation down, regardless of
  // which code path triggered it.
  static IMPERSONATION_KEYS = {
    SUPERUSER_TOKEN: "vivoSuperuserToken",
    IMPERSONATED_USER: "vivoImpersonatedUser",
  };

  static ENDPOINTS = {
    LOGIN: "/auth/login/", // TODO(VIVO-XXXX): change to /mfa/login/ after MFA is rolled out — create a Jira story before acting on this
    // LOGIN: "/mfa/login/",
    REFRESH: "/auth/token/refresh",
    USER_INFO: "/auth/userinfo/",
    MFA_ENABLE: "/auth/mfa/enable/",
    MFA_VERIFY: "/auth/mfa/verify/",
    SIGNUP: "/auth/signup/",
    FORGOT_PASSWORD: "/auth/password/forgot/",
    RESET_PASSWORD: "/auth/password/reset/",
    // IAM endpoints
    IAM_EXCHANGE: "/auth/exchange",
    IAM_LOGOUT: "/auth/logout",
  };

  baseUrl: string;
  iamUrl: string;
  tokenRefreshThreshold: number;
  refreshTimer:
    | ReturnType<typeof setTimeout>
    | ReturnType<typeof setInterval>
    | null;
  refreshPromise: Promise<void> | null;
  visibilityChangeHandler: (() => void) | null;
  storage: Storage;

  constructor(config: AuthServiceConfig = {}) {
    this.baseUrl = config.baseUrl || import.meta.env.VITE_API_URL;
    this.iamUrl = config.iamUrl || import.meta.env.VITE_IAM_URL;
    this.tokenRefreshThreshold = config.tokenRefreshThreshold || 300; // 5 minutes
    this.refreshTimer = null; // Initialize the refreshTimer variable
    this.refreshPromise = null; // Deduplicate concurrent refresh calls
    this.visibilityChangeHandler = null; // Store a reference to the visibility change handler
    this.storage = config.storage || sessionStorage;

    // Bind methods to ensure correct 'this' context
    // this.resetIdleTimer = this.resetIdleTimer.bind(this);
    // this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  // Private helper methods
  #validateTokenFormat(token: string): void {
    if (!token || typeof token !== "string" || !token.split(".")[1]) {
      throw new Error("Invalid token format");
    }
  }

  #getFullUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`;
  }

  async #fetchWithAuth(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Record<string, unknown> & { status: number }> {
    try {
      const response = await fetch(endpoint, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new ApiError(
          `HTTP error! status: ${response.status}`,
          response.status,
        );
      }
      const data = await response.json();
      data.status = response.status;
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(`Network error: ${(error as Error).message}`, 500);
    }
  }

  getToken(type: string): string | null {
    //Read token stored in session storage and returns it
    return this.storage.getItem(type);
  }

  setToken(type: string, value: string): void {
    if (!value) {
      throw new Error(`Cannot set ${type} to null or empty value`);
    }
    this.storage.setItem(type, value);
  }

  removeToken(type: string): void {
    this.storage.removeItem(type);
  }

  // End any active impersonation by discarding the stashed superuser token and
  // the persisted impersonated user. Called on logout so a superuser is never
  // left with stale impersonation state (e.g. a lingering banner) on their next
  // session.
  endImpersonation(): void {
    this.storage.removeItem(AuthService.IMPERSONATION_KEYS.SUPERUSER_TOKEN);
    this.storage.removeItem(AuthService.IMPERSONATION_KEYS.IMPERSONATED_USER);
  }

  // True while a superuser impersonation is active. The stashed superuser token
  // only exists between startImpersonation and stopImpersonation, so its
  // presence is the authoritative signal that the current access token is an
  // impersonation token. That token must never be refreshed: the refresh token
  // still belongs to the superuser, so a refresh would silently swap the
  // session back to the superuser while the UI still shows the impersonation
  // banner.
  isImpersonating(): boolean {
    return !!this.getToken(AuthService.IMPERSONATION_KEYS.SUPERUSER_TOKEN);
  }

  decodeToken(token: string): TokenPayload {
    try {
      this.#validateTokenFormat(token);
      return JSON.parse(window.atob(token.split(".")[1])) as TokenPayload;
    } catch (error) {
      throw new Error("Failed to decode token: " + (error as Error).message);
    }
  }

  isTokenExpired(token: string | null): boolean {
    //Verifies that token is expired or not
    if (!token) return true;

    try {
      //Decode token to get expiration time
      const { exp } = this.decodeToken(token);
      //If current time is greater than expiration time, return true
      return Date.now() / 1000 >= exp;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true;
    }
  }

  //Authentication state methods
  async isAuthenticated(): Promise<{ authenticated: boolean }> {
    const accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
    if (accessToken && !this.isTokenExpired(accessToken)) {
      return { authenticated: true };
    }

    // The impersonation token expires naturally (refresh is intentionally
    // blocked). If it's already expired by the time we get here, clean up the
    // stale keys rather than falling through to the refresh branch, which would
    // no-op and leave storage in a broken state.
    if (this.isImpersonating()) {
      this.endImpersonation();
      return { authenticated: false };
    }

    // Access token missing or expired — attempt refresh before giving up
    const refreshToken = this.getToken(AuthService.TOKEN_TYPES.REFRESH);
    if (refreshToken) {
      try {
        await this.refreshAccessToken();
        const newToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
        return {
          authenticated: !!(newToken && !this.isTokenExpired(newToken)),
        };
      } catch {
        return { authenticated: false };
      }
    }

    return { authenticated: false };
  }

  async refreshAccessToken(): Promise<void> {
    // Never refresh an impersonation access token (see isImpersonating). This
    // guard is the single choke point that protects every refresh path —
    // scheduled timers, idle/visibility wake-ups, isAuthenticated() on route
    // mount, and getUserInfo() — from silently reverting to the superuser.
    if (this.isImpersonating()) {
      return;
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        //1. Read stored refresh token to request a new access token
        const refreshToken = this.getToken(AuthService.TOKEN_TYPES.REFRESH);

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        //2. Fetch new access token from IAM
        const data = await this.#fetchWithAuth(
          `${this.iamUrl}${AuthService.ENDPOINTS.REFRESH}`,
          {
            method: "POST",
            body: JSON.stringify({ refresh_token: refreshToken }),
          },
        );

        if (data.access_token) {
          this.setToken(
            AuthService.TOKEN_TYPES.ACCESS,
            data.access_token as string,
          );
          this.scheduleTokenRefresh();
        }
      } catch (error) {
        console.error("Error refreshing access token:", error);
        // Handle token refresh error, such as logout
        this.logout();
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  scheduleTokenRefresh = (): void => {
    // Clear any existing timeout to avoid multiple scheduled refreshes
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Don't arm a refresh while impersonating; the token must not be refreshed.
    if (this.isImpersonating()) {
      this.refreshTimer = null;
      return;
    }

    const accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
    //
    if (!accessToken) return;

    try {
      const { exp } = this.decodeToken(accessToken);
      const now = Date.now() / 1000;
      const timeUntilRefresh = (exp - now - 60) * 1000; // Refresh 1 minute before expiration

      if (timeUntilRefresh <= 0) {
        //
        this.refreshAccessToken();
        return;
      }

      // Fetch new access token just before the current one expires
      this.refreshTimer = setTimeout(
        () => this.refreshAccessToken(),
        timeUntilRefresh,
      );
    } catch (error) {
      console.error("Error scheduling token refresh:", error);
    }
  };

  resetIdleTimer(): void {
    // While impersonating, idle/visibility activity must not refresh or
    // re-schedule the impersonation token.
    if (this.isImpersonating()) return;

    // Refresh the token if it's close to expiring (e.g., within 5 minutes)
    const accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
    if (accessToken) {
      const { exp } = this.decodeToken(accessToken);
      const now = Date.now() / 1000;
      if (exp - now < this.tokenRefreshThreshold) {
        // 5 minutes
        this.refreshAccessToken();
      }
    }

    // Reschedule the token refresh
    this.scheduleTokenRefresh();
  }

  initVisibilityChangeListener(): void {
    // Remove any previously registered handler before adding a new one to
    // prevent duplicate listeners if this is called more than once.
    this.removeVisibilityChangeListener();

    this.visibilityChangeHandler = () => {
      if (document.visibilityState === "visible") {
        // Reset the idle timer when the user comes back to the page
        this.resetIdleTimer();
      }
    };

    // Add the event listener for visibility change
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);
  }

  cancelRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  removeVisibilityChangeListener(): void {
    if (this.visibilityChangeHandler) {
      // Remove the visibility change listener using the stored reference
      document.removeEventListener(
        "visibilitychange",
        this.visibilityChangeHandler,
      );
      this.visibilityChangeHandler = null;
    }
  }

  async getAccessToken(): Promise<string | null> {
    //
    //Read access token from storage and validates if it is expired. Then, return accessToken

    //1. Read access token from storage
    let accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);

    //2. Check if the token has expired, then return unexpired token
    if (!accessToken || this.isTokenExpired(accessToken)) {
      // Never attempt to refresh an impersonation token — the refresh token
      // belongs to the superuser and would silently swap the session.
      if (!this.isImpersonating()) {
        await this.refreshAccessToken();
        accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
      }
    } else {
      // If the token is valid, ensure a refresh is scheduled
      this.scheduleTokenRefresh();
    }
    return accessToken;
  }

  startTokenRefreshInterval(): void {
    //
    // Refresh the token every 5 minutes
    const refreshTokenInterval = 5 * 60 * 1000; // 5 minutes in milliseconds

    this.refreshTimer = setInterval(() => {
      //
      this.refreshAccessToken();
    }, refreshTokenInterval);
  }

  handleSuccessfulMfaSetup(
    status: number,
    data: Record<string, unknown>,
  ): Record<string, unknown> {
    const { access, refresh } = data as { access: string; refresh: string };
    this.setToken(AuthService.TOKEN_TYPES.ACCESS, access);
    this.setToken(AuthService.TOKEN_TYPES.REFRESH, refresh);

    // Schedule next token refresh
    this.scheduleTokenRefresh();
    return { ...data, status };
  }

  async getUserInfo(): Promise<unknown> {
    const url = this.#getFullUrl(AuthService.ENDPOINTS.USER_INFO);

    let accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);

    if (accessToken && this.isTokenExpired(accessToken)) {
      const refreshToken = this.getToken(AuthService.TOKEN_TYPES.REFRESH);
      if (refreshToken && !this.isImpersonating()) {
        try {
          await this.refreshAccessToken();
          accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
        } catch {
          console.warn("Failed to refresh access token for user info request");
          accessToken = null;
        }
      } else {
        accessToken = null;
      }
    }

    if (accessToken && !this.isTokenExpired(accessToken)) {
      let tokenResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (
        tokenResponse.status === 401 &&
        this.getToken(AuthService.TOKEN_TYPES.REFRESH) &&
        !this.isImpersonating()
      ) {
        try {
          await this.refreshAccessToken();
          accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
          if (accessToken && !this.isTokenExpired(accessToken)) {
            tokenResponse = await fetch(url, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
            });
          }
        } catch {
          console.warn("Failed to refresh token after 401 on user info");
        }
      }

      if (!tokenResponse.ok) {
        throw new Error("Error fetching user info with token");
      }
      const data = await tokenResponse.json();
      return data;
    }

    throw new Error("No valid access token available");
  }

  async getTrials(trialId: string): Promise<unknown> {
    const url = `${(this as unknown as { dataBaseUrl: string }).dataBaseUrl}/data/trial/${trialId}/`;

    try {
      const accessToken = this.getToken(AuthService.TOKEN_TYPES.ACCESS);
      if (!accessToken || this.isTokenExpired(accessToken)) {
        throw new Error("No valid access token available");
      }
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error("Error fetching trials");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching trials:", error);
    }
  }

  async login(values: {
    email: string;
    password: string;
  }): Promise<Record<string, unknown> | undefined> {
    const url = this.#getFullUrl(AuthService.ENDPOINTS.LOGIN);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: values.email,
          password: values.password,
        }),
      });

      const data = await response.json();

      switch (response.status) {
        case 200: //Valid credentials, MFA enabled in a trusted device
          return this.handleSuccessfulMfaSetup(response.status, data);
        case 202: //Valid credentials, MFA enabled
          return { ...data, status: response.status };
        case 401: //Invalid credentials or user doesn't exist
          return { ...data, status: response.status };
        case 403: //MFA not enabled
          return { ...data, status: response.status };
        default:
          throw new Error("Login failed", data.status);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async enableMfa(values: {
    code: string;
  }): Promise<Record<string, unknown> | undefined> {
    //Enabling MFA
    try {
      const response = await fetch(
        this.#getFullUrl(AuthService.ENDPOINTS.MFA_ENABLE),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            // secret: values.secret,
            totp: values.code,
          }),
        },
      );
      const data = await response.json();

      switch (response.status) {
        case 201: //MFA enabled
        case 200: //MFA enabled
          return this.handleSuccessfulMfaSetup(response.status, data);
        case 401: //Invalid code or secret or MFA already enabled
          return { ...data, status: response.status };
        default:
          throw new Error(`MFA setup failed: ${response.status}`);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async verifyMfaCode(payload: {
    code: string;
    trustedDevice: boolean;
  }): Promise<Record<string, unknown>> {
    try {
      const data = await this.#fetchWithAuth(
        this.#getFullUrl(AuthService.ENDPOINTS.MFA_VERIFY),
        {
          method: "POST",
          body: JSON.stringify({
            totp: payload.code,
            trustDevice: payload.trustedDevice,
          }),
        },
      );

      if (data.access && data.refresh) {
        this.setToken(AuthService.TOKEN_TYPES.ACCESS, data.access as string);
        this.setToken(AuthService.TOKEN_TYPES.REFRESH, data.refresh as string);
        this.scheduleTokenRefresh();
      }

      return data;
    } catch (error) {
      // if (error instanceof ApiError && error.status === 400) {
      //   return {
      //     error: 'Invalid verification code',
      //     status: 400
      //   };
      // }
      if (error instanceof ApiError && error.status === 401) {
        return {
          error: "Failed to verify code",
          status: 401,
        };
      }
      if (error instanceof ApiError && error.status === 500) {
        return {
          error: "Server error",
          status: 500,
        };
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    // Use IAM logout
    return this.logoutViaIAM();
  }

  async signup(values: Record<string, unknown>): Promise<boolean> {
    try {
      const response = await fetch(
        this.#getFullUrl(AuthService.ENDPOINTS.SIGNUP),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        },
      );

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(errorMessage);
      }

      await response.json();

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  forgotPassword = async (email: string): Promise<boolean> => {
    try {
      const response = await fetch(
        this.#getFullUrl(AuthService.ENDPOINTS.FORGOT_PASSWORD),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(errorMessage);
      }

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  resetPassword = async (
    payload: Record<string, unknown>,
  ): Promise<boolean> => {
    try {
      const response = await fetch(
        this.#getFullUrl(AuthService.ENDPOINTS.RESET_PASSWORD),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(errorMessage);
      }

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  // IAM Authentication Methods

  /**
   * Get the IAM login URL with return_to parameter
   * @param {string} returnTo - The URL to redirect back to after login (defaults to current URL)
   * @returns {string} - The full IAM login URL
   */
  getIAMLoginUrl(returnTo: string = window.location.href): string {
    const encodedReturnTo = encodeURIComponent(returnTo);
    return `${this.iamUrl}/login?return_to=${encodedReturnTo}`;
  }

  /**
   * Redirect the user to the IAM login page
   * @param {string} returnTo - The URL to redirect back to after login (defaults to current URL)
   */
  redirectToIAMLogin(returnTo: string = window.location.href): void {
    window.location.href = this.getIAMLoginUrl(returnTo);
  }

  /**
   * Exchange an authorization code for access and refresh tokens
   * @param {string} code - The authorization code from the IAM redirect
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async exchangeCodeForTokens(
    code: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(
        `${this.iamUrl}${AuthService.ENDPOINTS.IAM_EXCHANGE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code: code }),
        },
      );

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(
          errorData.message || `Exchange failed with status ${response.status}`,
        );
      }

      const data = (await response.json()) as {
        access_token?: string;
        refresh_token?: string;
      };

      // Store the tokens
      if (data.access_token) {
        this.setToken(AuthService.TOKEN_TYPES.ACCESS, data.access_token);
      }
      if (data.refresh_token) {
        this.setToken(AuthService.TOKEN_TYPES.REFRESH, data.refresh_token);
      }

      // Schedule token refresh
      this.scheduleTokenRefresh();

      return { success: true };
    } catch (error) {
      console.error("Error exchanging code for tokens:", error);
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * Logout via the IAM service by revoking the refresh token
   * @param {boolean} revokeAll - If true, revoke all tokens for the user
   */
  async logoutViaIAM(revokeAll: boolean = false): Promise<void> {
    try {
      const refreshToken = this.getToken(AuthService.TOKEN_TYPES.REFRESH);

      // Call IAM logout endpoint to revoke the refresh token
      if (refreshToken) {
        await fetch(`${this.iamUrl}${AuthService.ENDPOINTS.IAM_LOGOUT}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            refresh_token: refreshToken,
            ...(revokeAll && { all: true }),
          }),
        }).catch((err) => {
          console.error("Error calling IAM logout endpoint:", err);
        });
      }

      // Clear local tokens, impersonation state, and timers
      this.removeToken(AuthService.TOKEN_TYPES.ACCESS);
      this.removeToken(AuthService.TOKEN_TYPES.REFRESH);
      this.endImpersonation();
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }

      // Redirect to IAM login
      this.redirectToIAMLogin(window.location.origin + "/dashboard");
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if logout fails, clear local state and redirect
      this.removeToken(AuthService.TOKEN_TYPES.ACCESS);
      this.removeToken(AuthService.TOKEN_TYPES.REFRESH);
      this.endImpersonation();
      this.redirectToIAMLogin(window.location.origin + "/dashboard");
    }
  }
}

export const TOKEN_TYPES = AuthService.TOKEN_TYPES;
export const IMPERSONATION_KEYS = AuthService.IMPERSONATION_KEYS;
const authService = new AuthService();
export default authService;
